import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserPlus, Eye, CalendarCheck, TrendingUp, Route, CheckCircle2,
  MapPin, Star, Tag, Clock, ArrowRight, ShieldCheck, Check,
  TrendingUpIcon, Users, ChevronRight, Search, Signal, Wifi, Battery
} from 'lucide-react';
import landingData from '../landingData.json';

// Step 1 UI Screen: Partner Registration & Listing
const Step1Screen = () => (
  <div className="w-full h-full bg-slate-50 flex flex-col p-4 text-left select-none overflow-y-auto">
    {/* iOS Status Bar */}
    <div className="flex items-center justify-between text-[11px] font-semibold text-slate-800 pt-1 pb-3 px-1">
      <span>09:41</span>
      <div className="flex items-center gap-1.5 text-slate-700">
        <Signal size={12} />
        <Wifi size={12} />
        <Battery size={14} />
      </div>
    </div>

    {/* App Header */}
    <div className="flex items-center justify-between mb-3">
      <div>
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#067978]">Step 01</span>
        <h4 className="text-base font-extrabold text-slate-900 leading-tight">Partner Setup</h4>
      </div>
      <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-[#044E4D] text-[10px] font-extrabold flex items-center gap-1">
        <ShieldCheck size={12} /> Live Verified
      </span>
    </div>

    {/* Store Profile Card */}
    <div className="bg-white rounded-2xl p-3.5 shadow-sm border border-slate-100 space-y-3 mb-3">
      <div className="h-24 bg-gradient-to-r from-[#067978] to-[#044E4D] rounded-xl flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:12px_12px]"></div>
        <div className="text-white text-center z-10">
          <span className="text-xs font-bold uppercase tracking-wider bg-white/20 px-2.5 py-0.5 rounded-full">Restaurant & Cafe</span>
        </div>
      </div>

      <div className="space-y-1">
        <h5 className="font-extrabold text-sm text-slate-900">The Oceanfront Bistro</h5>
        <p className="text-[11px] text-slate-500 flex items-center gap-1 font-medium">
          <MapPin size={11} className="text-[#067978]" /> Calangute Beach, Goa • Open 10:00 - 23:00
        </p>
      </div>

      <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
        <span className="text-slate-500 font-semibold">Listing Status</span>
        <span className="text-emerald-600 font-bold flex items-center gap-1">
          <Check size={12} /> 100% Complete
        </span>
      </div>
    </div>

    {/* Setup Checklist */}
    <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-100 space-y-2 mb-3">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Setup Checklist</span>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-700">
          <div className="w-4 h-4 rounded-full bg-emerald-100 text-[#067978] flex items-center justify-center text-[10px]">✓</div>
          Business Profile & Photos
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-700">
          <div className="w-4 h-4 rounded-full bg-emerald-100 text-[#067978] flex items-center justify-center text-[10px]">✓</div>
          15% Traveller Exclusive Offer
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-700">
          <div className="w-4 h-4 rounded-full bg-emerald-100 text-[#067978] flex items-center justify-center text-[10px]">✓</div>
          Instant QR Voucher Scanner
        </div>
      </div>
    </div>

    <button className="w-full py-2.5 bg-[#067978] text-white font-bold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1 mt-auto">
      Ready for Travellers <ArrowRight size={14} />
    </button>
  </div>
);

// Step 2 UI Screen: Traveller App Discovery
const Step2Screen = () => (
  <div className="w-full h-full bg-slate-50 flex flex-col p-4 text-left select-none overflow-y-auto">
    {/* iOS Status Bar */}
    <div className="flex items-center justify-between text-[11px] font-semibold text-slate-800 pt-1 pb-3 px-1">
      <span>09:41</span>
      <div className="flex items-center gap-1.5 text-slate-700">
        <Signal size={12} />
        <Wifi size={12} />
        <Battery size={14} />
      </div>
    </div>

    {/* Traveller App Header */}
    <div className="bg-white rounded-xl p-2.5 shadow-sm border border-slate-100 flex items-center gap-2 mb-3">
      <Search size={14} className="text-slate-400" />
      <span className="text-xs text-slate-400 font-medium">Explore Goa dining & activities...</span>
    </div>

    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-800">Featured Near You</span>
      <span className="text-[10px] text-[#067978] font-bold">350m Away</span>
    </div>

    {/* Traveller Discovery Card */}
    <div className="bg-white rounded-2xl p-3 shadow-md border border-slate-100 space-y-2.5 mb-3">
      <div className="h-28 bg-gradient-to-tr from-[#044E4D] via-[#067978] to-teal-400 rounded-xl relative overflow-hidden p-3 flex flex-col justify-between">
        <span className="self-end bg-white/90 backdrop-blur-md px-2 py-0.5 rounded-full text-[10px] font-extrabold text-[#044E4D] flex items-center gap-1">
          <Star size={10} className="fill-amber-400 text-amber-400" /> 4.9 (128 reviews)
        </span>
        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-teal-200">Recommended Partner</span>
          <h5 className="font-extrabold text-sm text-white leading-tight">The Oceanfront Bistro</h5>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs pt-1">
        <span className="text-slate-500 font-medium flex items-center gap-1">
          <MapPin size={12} className="text-[#067978]" /> Calangute Beach
        </span>
        <span className="text-emerald-600 font-extrabold text-[11px]">Open Now</span>
      </div>

      {/* Special Offer Badge */}
      <div className="bg-[#E0F2F1] border border-[#067978]/25 rounded-xl p-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-[#067978] text-white rounded-lg">
            <Tag size={14} />
          </div>
          <div>
            <h6 className="text-[11px] font-extrabold text-[#044E4D] leading-none">15% Off Total Bill</h6>
            <p className="text-[9px] text-teal-700 font-medium mt-0.5">Exclusive Trrip Voucher</p>
          </div>
        </div>
        <span className="px-2 py-1 bg-[#067978] text-white font-extrabold text-[10px] rounded-lg shadow-2xs">
          Get Offer
        </span>
      </div>
    </div>

    <div className="bg-[#044E4D]/5 border border-[#067978]/15 rounded-xl p-2.5 flex items-center gap-2">
      <div className="p-1.5 bg-[#067978] text-white rounded-lg">
        <Users size={14} />
      </div>
      <p className="text-[10px] text-slate-700 font-semibold leading-tight">
        <strong className="text-[#067978]">42 Travellers</strong> viewed this place in the last 2 hours.
      </p>
    </div>
  </div>
);

// Step 3 UI Screen: Partner Live Bookings
const Step3Screen = () => (
  <div className="w-full h-full bg-slate-50 flex flex-col p-4 text-left select-none overflow-y-auto">
    {/* iOS Status Bar */}
    <div className="flex items-center justify-between text-[11px] font-semibold text-slate-800 pt-1 pb-3 px-1">
      <span>09:41</span>
      <div className="flex items-center gap-1.5 text-slate-700">
        <Signal size={12} />
        <Wifi size={12} />
        <Battery size={14} />
      </div>
    </div>

    {/* Header */}
    <div className="flex items-center justify-between mb-3">
      <div>
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#067978]">Step 03</span>
        <h4 className="text-base font-extrabold text-slate-900 leading-tight">New Booking Requests</h4>
      </div>
      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-extrabold">
        2 Pending
      </span>
    </div>

    {/* Booking Request Card 1 */}
    <div className="bg-white rounded-2xl p-3.5 shadow-md border-2 border-[#067978]/30 space-y-3 mb-3">
      <div className="flex items-start justify-between">
        <div>
          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-[#067978] text-[9px] font-extrabold uppercase tracking-wider">
            Instant Request
          </span>
          <h5 className="font-extrabold text-sm text-slate-900 mt-1">Rahul Sharma (2 Guests)</h5>
          <p className="text-[10px] text-slate-400 font-medium">Table Booking + 15% Voucher</p>
        </div>
        <span className="text-xs font-extrabold text-[#067978]">₹1,250</span>
      </div>

      <div className="bg-slate-50 rounded-xl p-2 flex items-center justify-between text-[10px] text-slate-600 font-semibold">
        <span className="flex items-center gap-1"><Clock size={11} className="text-[#067978]" /> Today, 19:30</span>
        <span className="text-slate-400">Voucher #TRP-9842</span>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button className="py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] rounded-xl transition-colors">
          Decline
        </button>
        <button className="py-2 bg-[#067978] text-white font-extrabold text-[11px] rounded-xl shadow-sm flex items-center justify-center gap-1">
          Accept <Check size={12} />
        </button>
      </div>
    </div>

    {/* Confirmed Booking Card 2 */}
    <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-100 flex items-center justify-between">
      <div className="space-y-0.5">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
          <h6 className="font-bold text-xs text-slate-900">Priya Patel (4 Guests)</h6>
        </div>
        <p className="text-[10px] text-slate-400 font-medium">Confirmed • Today, 20:15</p>
      </div>
      <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-extrabold rounded-lg">
        Accepted
      </span>
    </div>
  </div>
);

// Step 4 UI Screen: Business Growth & Analytics
const Step4Screen = () => (
  <div className="w-full h-full bg-slate-50 flex flex-col p-4 text-left select-none overflow-y-auto">
    {/* iOS Status Bar */}
    <div className="flex items-center justify-between text-[11px] font-semibold text-slate-800 pt-1 pb-3 px-1">
      <span>09:41</span>
      <div className="flex items-center gap-1.5 text-slate-700">
        <Signal size={12} />
        <Wifi size={12} />
        <Battery size={14} />
      </div>
    </div>

    {/* Header */}
    <div className="flex items-center justify-between mb-3">
      <div>
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#067978]">Step 04</span>
        <h4 className="text-base font-extrabold text-slate-900 leading-tight">Partner Growth</h4>
      </div>
      <span className="px-2.5 py-1 rounded-full bg-[#E0F2F1] text-[#044E4D] text-[10px] font-extrabold">
        July Overview
      </span>
    </div>

    {/* Revenue Growth Card */}
    <div className="bg-gradient-to-br from-[#044E4D] to-[#067978] rounded-2xl p-4 text-white shadow-md space-y-3 mb-3 relative overflow-hidden">
      <div className="flex items-center justify-between relative z-10">
        <span className="text-[10px] font-bold uppercase tracking-wider text-teal-200">Total Partner Revenue</span>
        <span className="px-2 py-0.5 bg-white/20 backdrop-blur-md rounded-full text-[10px] font-bold text-white flex items-center gap-1">
          <TrendingUpIcon size={10} /> +42.5%
        </span>
      </div>

      <div className="relative z-10">
        <h3 className="text-2xl font-extrabold tracking-tight">₹48,250</h3>
        <p className="text-[10px] text-teal-100 font-medium">Generated via Trrip Travellers</p>
      </div>

      {/* SVG Growth Sparkline */}
      <div className="h-10 w-full pt-1">
        <svg className="w-full h-full overflow-visible" viewBox="0 0 100 30">
          <path
            d="M0,25 Q20,18 40,22 T80,8 T100,2"
            fill="none"
            stroke="#5eead4"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>

    {/* Metrics Grid */}
    <div className="grid grid-cols-2 gap-2.5 mb-3">
      <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 space-y-1">
        <span className="text-[10px] font-bold uppercase text-slate-400">Traveller Visits</span>
        <h5 className="text-base font-extrabold text-slate-900">348</h5>
        <span className="text-[9px] text-emerald-600 font-bold">+28% this month</span>
      </div>
      <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 space-y-1">
        <span className="text-[10px] font-bold uppercase text-slate-400">Repeat Customers</span>
        <h5 className="text-base font-extrabold text-slate-900">84</h5>
        <span className="text-[9px] text-emerald-600 font-bold">High Loyalty</span>
      </div>
    </div>

    {/* Top Offer Performance */}
    <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 flex items-center justify-between">
      <div className="space-y-0.5">
        <h6 className="font-bold text-xs text-slate-900">15% Traveller Special</h6>
        <p className="text-[10px] text-slate-400 font-medium">182 Redemptions</p>
      </div>
      <ChevronRight size={16} className="text-slate-400" />
    </div>
  </div>
);

const stepScreens = [
  <Step1Screen key="0" />,
  <Step2Screen key="1" />,
  <Step3Screen key="2" />,
  <Step4Screen key="3" />
];

const stepIcons = [
  <UserPlus key="0" size={20} />,
  <Eye key="1" size={20} />,
  <CalendarCheck key="2" size={20} />,
  <TrendingUp key="3" size={20} />
];

const HowItWorksSection = () => {
  const [activeStep, setActiveStep] = useState(0);

  // Auto advance steps every 6 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % landingData.howItWorksSteps.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-24 bg-white border-t border-b border-gray-100 relative overflow-hidden" id="how">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="flex justify-center mb-2">
            <div className="inline-flex items-center gap-2 text-[11px] font-extrabold tracking-widest uppercase px-4 py-1.5 rounded-full text-[#044E4D] bg-[#E0F2F1] border border-[#067978]/25">
              <Route size={14} className="text-[#067978]" /> How it works
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0F172A] tracking-tight leading-tight">
            From listing to bookings <br />
            <span className="text-[#067978]">in four simple steps.</span>
          </h2>
          <p className="text-[#475569] font-normal text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Zero technical setup. Zero upfront cost. You could be live before your next cup of chai.
          </p>
        </div>

        {/* 2-Column Interactive Split Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: 4 Interactive Step Cards */}
          <div className="lg:col-span-6 space-y-4">
            {landingData.howItWorksSteps.map((step, idx) => {
              const isActive = activeStep === idx;

              return (
                <motion.div
                  key={idx}
                  onClick={() => setActiveStep(idx)}
                  whileHover={{ scale: 1.01 }}
                  className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 border ${
                    isActive 
                      ? 'bg-[#F0FAF8] border-[#067978] shadow-md' 
                      : 'bg-white border-gray-100 hover:border-gray-200 hover:bg-slate-50/60'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Step Icon Badge */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                      isActive 
                        ? 'bg-[#067978] text-white shadow-sm' 
                        : 'bg-[#E0F2F1] text-[#067978]'
                    }`}>
                      {stepIcons[idx]}
                    </div>

                    <div className="flex-1 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-extrabold tracking-widest text-[#067978] uppercase">
                          Step {step.num}
                        </span>
                        <span className="text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full bg-white border border-[#067978]/20 text-[#044E4D]">
                          {step.tag}
                        </span>
                      </div>

                      <h3 className={`text-lg font-bold transition-colors ${
                        isActive ? 'text-[#067978]' : 'text-[#0F172A]'
                      }`}>
                        {step.title}
                      </h3>

                      <p className="text-sm text-gray-500 leading-relaxed font-normal">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Right Column: Authentic iPhone 16 Pro Titanium Frame holding React UI Screens */}
          <div className="lg:col-span-6 flex justify-center items-center relative py-6">
            
            {/* Ambient Background Glow */}
            <div className="absolute w-[380px] h-[380px] bg-[#067978]/15 rounded-full blur-3xl pointer-events-none"></div>

            {/* REALISTIC IPHONE 16 PRO FRAME */}
            <div className="relative w-[280px] sm:w-[320px] md:w-[340px] aspect-[9/19] rounded-[3rem] sm:rounded-[3.4rem] bg-[#0d131f] border-[8px] sm:border-[10px] border-[#1e293b] shadow-[0_25px_80px_rgba(6,121,120,0.22),0_10px_35px_rgba(0,0,0,0.35)] flex items-center justify-center overflow-hidden">
              
              {/* iPhone Side Hardware Buttons */}
              <div className="absolute -left-[12px] sm:-left-[14px] top-20 w-[4px] h-6 bg-[#334155] rounded-l-md z-50"></div>
              <div className="absolute -left-[12px] sm:-left-[14px] top-32 w-[4px] h-10 bg-[#334155] rounded-l-md z-50"></div>
              <div className="absolute -left-[12px] sm:-left-[14px] top-48 w-[4px] h-10 bg-[#334155] rounded-l-md z-50"></div>
              <div className="absolute -right-[12px] sm:-right-[14px] top-36 w-[4px] h-14 bg-[#334155] rounded-r-md z-50"></div>

              {/* Dynamic Island Notch (Apple iPhone Pill) */}
              <div className="absolute top-2.5 sm:top-3 w-24 sm:w-28 h-5 sm:h-6 bg-black rounded-full border border-slate-800/80 shadow-inner flex items-center justify-between px-3 z-50 pointer-events-none">
                <div className="w-2.5 h-2.5 bg-[#0a0f1d] rounded-full border border-slate-700"></div>
                <div className="w-2 h-2 bg-[#050810] rounded-full border border-slate-700"></div>
              </div>

              {/* iOS Home Indicator Bar */}
              <div className="absolute bottom-2 sm:bottom-3 w-28 sm:w-32 h-1 bg-slate-400 rounded-full z-50 pointer-events-none"></div>

              {/* Inner Display Screen Canvas */}
              <div className="w-full h-full rounded-[2.3rem] sm:rounded-[2.7rem] overflow-hidden bg-slate-50 relative flex items-center justify-center">
                
                {/* Subtle Ambient Glass Screen Reflection */}
                <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-white/10 via-transparent to-transparent pointer-events-none z-30"></div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.04 }}
                    transition={{ duration: 0.35 }}
                    className="w-full h-full"
                  >
                    {stepScreens[activeStep]}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Floating Status Pill Badge */}
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute -bottom-2 right-2 sm:right-10 bg-white border border-gray-100 rounded-2xl p-3 sm:p-4 shadow-xl flex items-center gap-2.5 sm:gap-3 z-50 max-w-[220px]"
            >
              <div className="p-1.5 sm:p-2 bg-emerald-50 text-[#067978] rounded-xl flex-shrink-0">
                <CheckCircle2 size={18} />
              </div>
              <div>
                <h4 className="text-[11px] sm:text-xs font-extrabold text-slate-800 leading-none">Instant Partner Setup</h4>
                <p className="text-[9px] sm:text-[10px] text-gray-400 font-bold mt-0.5 sm:mt-1">Live in 10 Minutes</p>
              </div>
            </motion.div>

          </div>

        </div>

        <p className="text-[11px] text-slate-400 font-normal italic mt-8 text-center select-none">
          *Artistic impression for illustration
        </p>

      </div>
    </section>
  );
};

export default HowItWorksSection;
